import { forwardRef, HTMLProps, ReactElement, ReactNode, useEffect, useRef } from "react";
import { classNames } from "../utils/classNames";
import {
  gridColsMapping,
  gridColsMappingLg,
  gridColsMappingMd,
  gridColsMappingSm,
  gridColsMappingXl,
  gridColSpanMapping,
  gridColSpanMappingLg,
  gridColSpanMappingMd,
  gridColSpanMappingSm,
  gridColSpanMappingXl,
  gridColSpanMappingXxl,
} from "./tailwindMappings";
import { IconName } from "./iconMapping";
import { IconProps } from "./Icon";
import { renderIcon } from "./iconHelpers";
import { isReactElement, mergeRefs } from "../utils/reactHelpers";
import { boxClassNames, BoxProps, CommonProps, commonPropsToClassName } from "./boxHelpers";
import { TMessage } from "../i18n/tt";
import { useTranslation } from "../stores/RootStoreContext";
import { clamp } from "lodash";

export type VBoxProps = Omit<BoxProps, "vertical">;

export type ContainerProps = VBoxProps;

export type GridProps = CommonProps & {
  children?: ReactNode;
  cols?: number;
  colsSm?: GridProps["cols"];
  colsMd?: GridProps["cols"];
  colsLg?: GridProps["cols"];
  colsXl?: GridProps["cols"];
  inline?: boolean;
  htmlProps?: Omit<HTMLProps<HTMLDivElement>, "className">;
};

type GridItemColSpan = "full" | number;

export type GridItemProps = CommonProps & {
  children?: ReactNode;
  colSpan?: GridItemColSpan;
  xs?: GridItemColSpan;
  sm?: GridItemColSpan;
  md?: GridItemColSpan;
  lg?: GridItemColSpan;
  xl?: GridItemColSpan;
  xxl?: GridItemColSpan;
};

export type CardProps = BoxProps & {
  icon?: IconName | IconProps;
  title?: string | ReactElement | TMessage;
  subTitle?: string | ReactElement | TMessage;
  action?: ReactElement;
};

export const Box = forwardRef<HTMLDivElement, BoxProps>(function Box(props, ref) {
  const { t } = useTranslation();
  const internalRef = useRef<HTMLDivElement>(null);
  const { children, htmlProps, testId, testType, tooltip, sticky, onPinnedChange } = props;

  useEffect(() => {
    if (sticky) {
      const observer = new IntersectionObserver(
        ([e]) => {
          onPinnedChange?.(e.intersectionRatio < 1);
        },
        { threshold: [1] },
      );
      if (internalRef.current) {
        observer.observe(internalRef.current);
      }
      return () => {
        observer.disconnect();
      };
    }
  }, [onPinnedChange, sticky]);

  return (
    <div
      ref={mergeRefs(ref, internalRef)}
      className={boxClassNames(props)}
      {...htmlProps}
      data-testid={testId}
      data-test-type={testType}
      data-tooltip={typeof tooltip === "string" ? tooltip : tooltip ? t(tooltip) : undefined}
    >
      {children}
    </div>
  );
});

export const VBox = forwardRef<HTMLDivElement, VBoxProps>(function VBox(props, ref) {
  return <Box ref={ref} {...props} vertical={true} />;
});

export const Container = forwardRef<HTMLDivElement, ContainerProps>(function Container(
  { container, ...props },
  ref,
) {
  return <VBox ref={ref} container={container ?? true} {...props} />;
});

const clampCols = (cols: number) => clamp(cols, 1, 12);
const clampColSpan = (cols: number | "full") => (cols === "full" ? cols : clamp(cols, 1, 12));

export const Grid = forwardRef<HTMLDivElement, GridProps>(function Grid(
  { children, cols, colsSm, colsMd, colsLg, colsXl, inline, testId, tooltip, htmlProps, ...props },
  ref,
) {
  const { t } = useTranslation();
  return (
    <div
      ref={ref}
      className={classNames(
        commonPropsToClassName(props),
        gridColsMapping[clampCols(cols ?? 12)],
        colsSm && gridColsMappingSm[clampCols(colsSm)],
        colsMd && gridColsMappingMd[clampCols(colsMd)],
        colsLg && gridColsMappingLg[clampCols(colsLg)],
        colsXl && gridColsMappingXl[clampCols(colsXl)],
        inline ? "tw-inline-grid" : "tw-grid",
      )}
      data-testid={testId}
      data-tooltip={typeof tooltip === "string" ? tooltip : tooltip ? t(tooltip) : undefined}
      {...htmlProps}
    >
      {children}
    </div>
  );
});

export const GridItem = forwardRef<HTMLDivElement, GridItemProps>(function GridItem(
  { children, colSpan, xs, sm, md, lg, xl, xxl, ...props },
  ref,
) {
  return (
    <div
      ref={ref}
      className={classNames(
        commonPropsToClassName(props),
        colSpan && gridColSpanMapping[clampColSpan(colSpan)],
        xs && gridColSpanMapping[clampColSpan(xs)],
        sm && gridColSpanMappingSm[clampColSpan(sm)],
        md && gridColSpanMappingMd[clampColSpan(md)],
        lg && gridColSpanMappingLg[clampColSpan(lg)],
        xl && gridColSpanMappingXl[clampColSpan(xl)],
        xxl && gridColSpanMappingXxl[clampColSpan(xxl)],
      )}
    >
      {children}
    </div>
  );
});

export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  { icon, title, subTitle, action, children, ...props },
  ref,
) {
  const { t } = useTranslation();
  return (
    <Box ref={ref} rounded padding bg dropShadow vertical {...props} testType={"Card"}>
      {(title || action) && (
        <VBox>
          <Box alignItems={"center"} justifyContent={"space-between"}>
            <h5 className={"tw-items-center tw-flex tw-gap-1"}>
              {icon && renderIcon(icon)}
              {typeof title === "string" || isReactElement(title) || !title ? title : t(title)}
            </h5>
            {action}
          </Box>
          {subTitle && (
            <div>
              {typeof subTitle === "string" || isReactElement(subTitle) || !subTitle
                ? subTitle
                : t(subTitle)}
            </div>
          )}
        </VBox>
      )}
      {children}
    </Box>
  );
});

/**
 * A spacer to ensure that the bottom-most components can be scrolled passed the "Chat" buttons
 */
export const BottomSpacer = forwardRef<HTMLDivElement>(function BottomSpacer(
  { children }: { children?: React.ReactNode },
  ref,
) {
  return (
    <div ref={ref} style={{ height: "4rem", flex: "none" }}>
      {children}
    </div>
  );
});
